import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/shadcn/dropdown-menu'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/shadcn/chart'
import { BarChart, Bar, CartesianGrid, XAxis } from 'recharts'
import { listMemoryLibraryIds } from '@/lib/api/memory'
import { fetchTelemetryTopSlowest, fetchTelemetryTimeseries, fetchTelemetryDistribution, type TelemetrySlowRequestItem, type TelemetryDistributionBucket } from '@/lib/api/telemetry'
import { Filter, RefreshCw } from 'lucide-react'

function toIso(v: string): string | undefined {
  if (!v) return undefined
  try {
    const d = new Date(v)
    if (isNaN(d.getTime())) return undefined
    return d.toISOString()
  } catch {
    return undefined
  }
}

function isoToLocal(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  const yyyy = d.getFullYear()
  const mm = pad(d.getMonth() + 1)
  const dd = pad(d.getDate())
  const hh = pad(d.getHours())
  const mi = pad(d.getMinutes())
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`
}

export default function UsageRequestsPage() {
  const { t } = useTranslation('common')
  const { data: allMemoryIds } = useQuery({ queryKey: ['memory-library-ids'], queryFn: () => listMemoryLibraryIds() })
  const [selectedMemoryIds, setSelectedMemoryIds] = useState<string[]>([])
  const [reqType, setReqType] = useState('')
  const [startLocal, setStartLocal] = useState<string>(() => {
    const d = new Date(); d.setHours(d.getHours() - 24); return isoToLocal(d.toISOString())
  })
  const [endLocal, setEndLocal] = useState<string>(() => isoToLocal(new Date().toISOString()))
  const [interval, setInterval] = useState<'minute' | 'hour' | 'day'>('hour')

  const common = useMemo(() => ({
    start: toIso(startLocal),
    end: toIso(endLocal),
    req_type: reqType || undefined,
    memory_ids: (selectedMemoryIds && selectedMemoryIds.length > 0) ? selectedMemoryIds : (allMemoryIds || []),
  }), [startLocal, endLocal, reqType, selectedMemoryIds, allMemoryIds])

  const { data: topSlowest } = useQuery({
    queryKey: ['telemetry-top-slowest', { ...common, limit: 100 }],
    queryFn: () => fetchTelemetryTopSlowest({ ...common, limit: 100 }),
    enabled: (allMemoryIds || []).length > 0,
  })

  const { data: distribution } = useQuery({
    queryKey: ['telemetry-distribution', { ...common, bins: 20 }],
    queryFn: () => fetchTelemetryDistribution({ ...common, bins: 20 }),
    enabled: (allMemoryIds || []).length > 0,
  })

  return (
    <div className="space-y-6 pt-8">
      <div className="flex flex-col items-stretch space-y-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('usage.requests.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('usage.requests.description')}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('usage.requests.filters.title')}</CardTitle>
          <CardDescription>{t('usage.requests.filters.description')}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-4">
          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.requests.filters.memoryVaults')}</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between">
                  <Filter className="mr-2 h-4 w-4" />
                  {selectedMemoryIds.length === (allMemoryIds?.length || 0) ? t('usage.requests.filters.all') : t('usage.requests.filters.selected', { count: selectedMemoryIds.length })}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 p-1">
                {(allMemoryIds || []).map((id) => (
                  <DropdownMenuCheckboxItem key={id} checked={selectedMemoryIds.includes(id)} onCheckedChange={(checked) => setSelectedMemoryIds((prev) => checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id))}>
                    <code className="text-xs">{id}</code>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.requests.filters.requestType')}</div>
            <Select value={reqType || '_all'} onValueChange={(v) => setReqType(v === '_all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder={t('usage.requests.filters.all')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">{t('usage.requests.filters.all')}</SelectItem>
                <SelectItem value="memory_query">memory_query</SelectItem>
                <SelectItem value="assistant_message">assistant_message</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.requests.filters.timeStart')}</div>
            <Input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.requests.filters.timeEnd')}</div>
            <div className="flex items-center gap-2">
              <Input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} />
              <Button variant="outline" size="icon" onClick={() => setEndLocal(isoToLocal(new Date().toISOString()))} title={t('usage.requests.filters.resetToCurrent')}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-sm text-muted-foreground">{t('usage.requests.filters.timeGranularity')}</div>
            <Select value={interval} onValueChange={(v) => (setInterval(v as any))}>
              <SelectTrigger>
                <SelectValue placeholder="hour" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minute">{t('usage.requests.filters.minute')}</SelectItem>
                <SelectItem value="hour">{t('usage.requests.filters.hour')}</SelectItem>
                <SelectItem value="day">{t('usage.requests.filters.day')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('usage.requests.charts.durationDistribution.title')}</CardTitle>
          <CardDescription>{t('usage.requests.charts.durationDistribution.description')}</CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:p-6">
          <ChartContainer config={{ count: { label: 'count', color: 'hsl(var(--chart-1))' } }} className="aspect-auto h-[260px] w-full">
            <BarChart data={distribution as TelemetryDistributionBucket[] | undefined} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
              <CartesianGrid vertical={false} />
              <XAxis dataKey={(d: any) => `${Math.round(d.start_ms)}-${Math.round(d.end_ms)}ms`} tickLine={false} axisLine={false} tickMargin={8} minTickGap={16} />
              <ChartTooltip content={<ChartTooltipContent className="w-[160px]" />} />
              <Bar dataKey="count" fill="var(--color-count)" />
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('usage.requests.charts.topSlowRequests.title')}</CardTitle>
          <CardDescription>{t('usage.requests.charts.topSlowRequests.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('usage.requests.charts.topSlowRequests.time')}</TableHead>
                <TableHead>{t('usage.requests.charts.topSlowRequests.type')}</TableHead>
                <TableHead>{t('usage.requests.charts.topSlowRequests.requestIdHint')}</TableHead>
                <TableHead>{t('usage.requests.charts.topSlowRequests.userHash')}</TableHead>
                <TableHead>{t('usage.requests.charts.topSlowRequests.durationMs')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(topSlowest || []).map((item: TelemetrySlowRequestItem, idx: number) => (
                <TableRow key={`${item.request_id_hint}-${idx}`}>
                  <TableCell>{new Date(item.timestamp).toLocaleString('zh-CN')}</TableCell>
                  <TableCell>{item.type}</TableCell>
                  <TableCell><code className="text-xs">{item.request_id_hint}</code></TableCell>
                  <TableCell><code className="text-xs">{item.user_hash}</code></TableCell>
                  <TableCell className="font-mono">{Math.round(item.duration_ms)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}


